import React, { FC } from 'react';
import { observer } from 'mobx-react';
import { Box, Typography } from '@mui/material';

import { ChatSearchResult } from '../../../services/apiService';
import { IChannel, IMessageAppealStrategy, IMessageClaimDetail } from '../../../types/chat';
import { useAppStore } from '../../../hooks/useAppStore';
import { ChatHelper } from '../../helpers/chatHelper';
import ChatThumbnail from './ChatThumbnail';

interface SearchResultsRendererProps {
  chatChannels: IChannel[];
  onSearchResultClick: (result: ChatSearchResult) => void;
  onChatSelected: (chatId: string, claimId: string | undefined) => void
}

const SearchResultsRenderer: FC<SearchResultsRendererProps> = observer(({
  onSearchResultClick,
  onChatSelected,
}) => {
  const { searchQuery, searchResults, searchLoading } = useAppStore();

  // Group search results by chat_id
  const groupSearchResultsByChat = (results: ChatSearchResult[]) => {
    const grouped = results.reduce((acc, result) => {
      if (!acc[result.chat_id]) {
        acc[result.chat_id] = {
          ...result,
          claim_number: result.claim_number,
          chat_id: result.chat_id,
          chat_title: result.chat_title,
          messages: []
        };
      }
      acc[result.chat_id].messages.push(result);
      return acc;
    }, {} as Record<string, { chat_id: string; chat_title: string; messages: ChatSearchResult[], claim_number?: string }>);

    return Object.values(grouped);
  };

  const getTextHighlights = (title: string, query: string): Array<{start: number; end: number}> => {
    if (!query || !title) return [];
    const lowerTitle = title.toLowerCase();
    const lowerQuery = query.toLowerCase().trim();
    const highlights: Array<{start: number; end: number}> = [];
    let index = 0;
    while ((index = lowerTitle.indexOf(lowerQuery, index)) !== -1) {
      highlights.push({ start: index, end: index + lowerQuery.length });
      index += lowerQuery.length;
    }
    return highlights;
  };

  // Condense a parsed ```json``` block (claim detail / appeal strategy) into a
  // single readable line, instead of showing the raw JSON syntax.
  const summarizeJsonContent = (content: IMessageClaimDetail | IMessageAppealStrategy): string => {
    const claimDetail = content as IMessageClaimDetail;
    if (claimDetail?.overview) {
      const { claim_id, patient_name, payer_name, total_billed, total_allowed } = claimDetail.overview;
      return [
        claim_id ? `Claim #${claim_id}` : null,
        patient_name,
        payer_name,
        total_billed != null ? `Billed $${total_billed}` : null,
        total_allowed != null ? `Allowed $${total_allowed}` : null,
      ].filter(Boolean).join(' · ');
    }

    const appealStrategy = content as IMessageAppealStrategy;
    if (appealStrategy?.disposition) {
      return [content?.title, appealStrategy.disposition].filter(Boolean).join(' · ');
    }

    return content?.title || 'Claim details';
  };

  // Highlight search terms in text
  const highlightSearchTerms = (text: string, highlights: Array<{start: number; end: number}>) => {
    if (!highlights || highlights.length === 0) {
      return text;
    }

    // Sort highlights by start position
    const sortedHighlights = [...highlights].sort((a, b) => a.start - b.start);
    
    let result = '';
    let lastIndex = 0;

    sortedHighlights.forEach((highlight, index) => {
      // Add text before highlight
      result += text.slice(lastIndex, highlight.start);
      
      // Add highlighted text with custom styling
      result += `<span style="
        overflow: hidden;
        color: var(--Black, #2D2D2D);
        text-overflow: ellipsis;
        font-family: Manrope;
        font-size: 0.875rem;
        font-style: italic;
        font-weight: bold;
        line-height: 1.25rem;
        text-decoration-line: underline;
        text-decoration-style: dotted;
        text-decoration-skip-ink: none;
        text-decoration-thickness: 8%;
        text-underline-offset: 15%;
        text-underline-position: from-font;
      ">${text.slice(highlight.start, highlight.end)}</span>`;
      
      lastIndex = highlight.end;
    });

    // Add remaining text
    result += text.slice(lastIndex);

    return result;
  };

  return (
    <Box sx={{ display: 'flex', px: 1, flexDirection: 'column', gap: 0.5, width: '100%' }}>
      <Typography sx={{ 
        color: '#2D2D2D', 
        fontWeight: 500, 
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        px: 1,
        py: 0.5,
      }}>
        Search Results
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, width: '100%' }}>
        {searchLoading ? (
          <Typography sx={{ px: 1, py: 0.5, color: '#666', }}>Searching...</Typography>
        ) : searchQuery.trim().length > 0 && searchQuery.trim().length < 3 ? (
          <Typography sx={{ px: 1, py: 0.5, color: '#666' }}>Type at least 3 characters to search</Typography>
        ) : searchResults.length > 0 ? (
          groupSearchResultsByChat(searchResults).map((chatGroup) => {
            const titleHighlights = getTextHighlights(chatGroup.chat_title, searchQuery);
            const titleHtml = titleHighlights.length > 0
              ? highlightSearchTerms(chatGroup.chat_title, titleHighlights)
              : undefined;
              
            return (
              <Box key={chatGroup.chat_id} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
                {/* Chat Header with Tag */}
                 <ChatThumbnail
                    chat={{...chatGroup, label: chatGroup.chat_title, name: chatGroup.chat_id, id: chatGroup.chat_id} as unknown as IChannel}
                    onChatDeleted={() => {}}
                    onChatPinned={() => {}}
                    onChatSelected={onChatSelected}
                    labelHtml={titleHtml}
                  />

                {/* Messages for this chat */}
                {chatGroup.messages.map((result) => {
                  const contentParts = ChatHelper.parseMessageContentParts(result.message.content);

                  return (
                    <Box
                      key={`${result.chat_id}-${result.message.id}`}
                      onClick={() => onSearchResultClick(result)}
                      sx={{
                        display: 'flex',
                        px: 1,
                        py: 1,
                        alignItems: 'flex-start',
                        gap: 1,
                        width: '100%',
                        cursor: 'pointer',
                        ml: 3,
                        '&:hover': {
                          bgcolor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
                        {contentParts.length > 0 ? contentParts.map((part, index) => {
                          const text = part.type === 'json'
                            ? summarizeJsonContent(part.content)
                            : part.content;

                          return (
                            <Typography
                              key={`${result.message.id}-part-${index}`}
                              variant='caption'
                              sx={{
                                color: '#666',
                                fontFamily: 'Manrope',
                                fontWeight: 400,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                              }}
                              dangerouslySetInnerHTML={{
                                __html: highlightSearchTerms(text, getTextHighlights(text, searchQuery)),
                              }}
                            />
                          );
                        }) : null}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            );
          })
        ) : searchQuery.trim().length >= 3 ? (
          <Box sx={{ px: 1, py: 0.5, color: '#666', fontSize: '0.875rem' }}>No results found</Box>
        ) : null}
      </Box>
    </Box>
  );
});

export default SearchResultsRenderer;

